// src/context/NotificationContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSocket } from "../hooks/use-socket";
import {getNotifications, markAllNotificationsAsRead} from "../api/api";
import { NotificationChannelResponse, NotificationDto } from "../types";
import { useAuth } from "./AuthContext";

interface NotificationContextType {
  notifications: NotificationDto[];
  unreadCount: number;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { socket } = useSocket();
  const { isAuthenticated, isLoading } = useAuth(); // ✅ used to delay≠≠ fetching
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = (id: number) => {
    setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      console.error("Failed to mark all notifications as read", err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const result = await getNotifications();
      setNotifications(result);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  // ✅ Only fetch notifications after auth context finishes loading
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    fetchNotifications();
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (!socket) return;

    const handler = (data: NotificationChannelResponse) => {
      const newNotification: NotificationDto = {
        id: data.id,
        title: data.title,
        message: data.message,
        type: data.type,
        user_id: 0,
        is_read: false,
        sending_type: "inter_app",
        created_at: new Date().toISOString(),
      };

      setNotifications((prev) => [newNotification, ...prev]);
    };

    socket.on("notification", handler);

    // ✅ Return a proper cleanup function
    return () => {
      socket.off("notification", handler);
    };
  }, [socket]);

  return (
      <NotificationContext.Provider
          value={{ notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications }}
      >
        {children}
      </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}
